// src/pages/Settings.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageTransition from "../components/PageTransition";
import axios from "axios";
import toast from "react-hot-toast";
import { BASE_ENDPOINT } from "../helpers/Config";
import {
    FaCog,
    FaSyncAlt,
    FaSlidersH,
    FaSave,
    FaPercentage,
    FaChartLine,
    FaUsers,
    FaMoneyBillWave,
    FaLayerGroup,
    FaEdit,
    FaTimes,
    FaCheck,
    FaLock,
    FaLockOpen,
    FaCoins,
    FaExchangeAlt,
    FaPlus,
    FaTrash,
    FaUniversity,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const EMPTY_UPI_FORM = {
    upi_id: "",
    min: "",
    max: "",
    provider: "",
    mid: "",
};

export default function Settings() {
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [saving, setSaving] = useState(false);

    const [settings, setSettings] = useState(null);
    const [formData, setFormData] = useState({
        trade_config: { win_rate: 50 },
        commission: {
            staking: {},
            self_deposit: 2,
            refer: {},
            deposit: {}
        },
        rate: {
            coin: 0,
            exchange: 0,
            exchange_sell: 0,
        }
    });
    const [exchangeUpiIds, setExchangeUpiIds] = useState([]);
    const [upiForm, setUpiForm] = useState(EMPTY_UPI_FORM);
    const [addingUpi, setAddingUpi] = useState(false);
    const [deletingUpiId, setDeletingUpiId] = useState(null);

    const navigate = useNavigate();
    const RESTRICTED_TRADE_LIST_USERNAMES = ["mking", "rking", "aking"];

    const username = localStorage.getItem("iutyeurds");
    const isRestricted = RESTRICTED_TRADE_LIST_USERNAMES.includes(username);

    useEffect(() => {
        if (isRestricted) {
            navigate("/");
        }
    }, [isRestricted, navigate]);

    // Edit mode states for each section
    const [editMode, setEditMode] = useState({
        trade: false,
        selfDeposit: false,
        staking: false,
        refer: false,
        deposit: false,
        rate: false
    });

    const token = useMemo(() => localStorage.getItem("token") || "", []);

    // Initialize staking levels
    const stakingLevels = useMemo(() =>
        Array.from({ length: 10 }, (_, i) => `level_${i + 1}`),
        []);

    // Initialize refer/deposit levels
    const referDepositLevels = useMemo(() =>
        Array.from({ length: 6 }, (_, i) => `level_${i + 1}`),
        []);

    const clampValue = (v, min = 0, max = 100) => {
        const n = Number(v);
        if (!Number.isFinite(n)) return min;
        return Math.max(min, Math.min(max, n));
    };

    const clampWinRate = (v) => clampValue(v, 1, 99);

    const clampRate = (v, min = 0, max = 999999) => {
        const n = Number(v);
        if (!Number.isFinite(n)) return min;
        return Math.max(min, Math.min(max, n));
    };

    // Function to disable mouse wheel on number inputs
    const disableNumberInputScroll = () => {
        document.querySelectorAll('input[type="number"]').forEach(input => {
            input.addEventListener('wheel', (e) => {
                e.preventDefault();
            });
        });
    };

    useEffect(() => {
        disableNumberInputScroll();

        // Reapply when DOM updates
        const observer = new MutationObserver(disableNumberInputScroll);
        observer.observe(document.body, { childList: true, subtree: true });

        return () => observer.disconnect();
    }, []);

    const fetchSettings = useCallback(async () => {
        if (!token) {
            setLoading(false);
            toast.error("Session missing. Please login again.");
            return;
        }

        setLoading(true);
        try {
            const res = await axios({
                method: "post",
                url: `${BASE_ENDPOINT}/admin/settings`,
                headers: { "Content-Type": "application/json" },
                data: { token },
                timeout: 15000,
            });

            const out = res?.data;

            if (out?.error) {
                if (out?.error === "session expired") {
                    toast.error("Session expired. Please login again.");
                } else {
                    toast.error(typeof out?.error === "string" ? out?.error : "Failed to fetch settings");
                }
                setSettings(null);
                setExchangeUpiIds([]);
                return;
            }

            const s = out?.data || null;
            setSettings(s);
            setExchangeUpiIds(Array.isArray(s?.exchange_upi_ids) ? s.exchange_upi_ids : []);

            // Initialize form data with fetched settings
            if (s) {
                setFormData({
                    trade_config: {
                        win_rate: clampWinRate(s?.trade_config?.win_rate || 50)
                    },
                    commission: {
                        staking: {
                            level_1: clampValue(s?.commission?.staking?.level_1 || 0),
                            level_2: clampValue(s?.commission?.staking?.level_2 || 0),
                            level_3: clampValue(s?.commission?.staking?.level_3 || 0),
                            level_4: clampValue(s?.commission?.staking?.level_4 || 0),
                            level_5: clampValue(s?.commission?.staking?.level_5 || 0),
                            level_6: clampValue(s?.commission?.staking?.level_6 || 0),
                            level_7: clampValue(s?.commission?.staking?.level_7 || 0),
                            level_8: clampValue(s?.commission?.staking?.level_8 || 0),
                            level_9: clampValue(s?.commission?.staking?.level_9 || 0),
                            level_10: clampValue(s?.commission?.staking?.level_10 || 0)
                        },
                        self_deposit: clampValue(s?.commission?.self_deposit || 0),
                        refer: {
                            level_1: clampValue(s?.commission?.refer?.level_1 || 0),
                            level_2: clampValue(s?.commission?.refer?.level_2 || 0),
                            level_3: clampValue(s?.commission?.refer?.level_3 || 0),
                            level_4: clampValue(s?.commission?.refer?.level_4 || 0),
                            level_5: clampValue(s?.commission?.refer?.level_5 || 0),
                            level_6: clampValue(s?.commission?.refer?.level_6 || 0)
                        },
                        deposit: {
                            level_1: clampValue(s?.commission?.deposit?.level_1 || 0),
                            level_2: clampValue(s?.commission?.deposit?.level_2 || 0),
                            level_3: clampValue(s?.commission?.deposit?.level_3 || 0),
                            level_4: clampValue(s?.commission?.deposit?.level_4 || 0),
                            level_5: clampValue(s?.commission?.deposit?.level_5 || 0),
                            level_6: clampValue(s?.commission?.deposit?.level_6 || 0)
                        }
                    },
                    rate: {
                        coin: clampRate(s?.rate?.coin ?? 0),
                        exchange: clampRate(s?.rate?.exchange ?? 0),
                        exchange_sell: clampRate(s?.rate?.exchange_sell ?? 0),
                    }
                });

                // Reset all edit modes when fetching new settings
                setEditMode({
                    trade: false,
                    selfDeposit: false,
                    staking: false,
                    refer: false,
                    deposit: false,
                    rate: false
                });
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to fetch settings");
            setSettings(null);
            setExchangeUpiIds([]);
        } finally {
            setLoading(false);
        }
    }, [token]);

    const onRefresh = useCallback(async () => {
        setRefreshing(true);
        try {
            await fetchSettings();
        } finally {
            setRefreshing(false);
        }
    }, [fetchSettings]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const isDirty = useMemo(() => {
        if (!settings) return false;

        // Check trade config
        if (clampWinRate(settings?.trade_config?.win_rate) !==
            clampWinRate(formData.trade_config.win_rate)) {
            return true;
        }

        // Check self deposit
        if (clampValue(settings?.commission?.self_deposit) !==
            clampValue(formData.commission.self_deposit)) {
            return true;
        }

        // Check staking levels
        for (let i = 1; i <= 10; i++) {
            const levelKey = `level_${i}`;
            if (clampValue(settings?.commission?.staking?.[levelKey]) !==
                clampValue(formData.commission.staking[levelKey])) {
                return true;
            }
        }

        // Check refer levels
        for (let i = 1; i <= 6; i++) {
            const levelKey = `level_${i}`;
            if (clampValue(settings?.commission?.refer?.[levelKey]) !==
                clampValue(formData.commission.refer[levelKey])) {
                return true;
            }
        }

        // Check deposit levels
        for (let i = 1; i <= 6; i++) {
            const levelKey = `level_${i}`;
            if (clampValue(settings?.commission?.deposit?.[levelKey]) !==
                clampValue(formData.commission.deposit[levelKey])) {
                return true;
            }
        }

        if (clampRate(settings?.rate?.coin) !== clampRate(formData.rate?.coin)) return true;
        if (clampRate(settings?.rate?.exchange) !== clampRate(formData.rate?.exchange)) return true;
        if (clampRate(settings?.rate?.exchange_sell) !== clampRate(formData.rate?.exchange_sell)) return true;

        return false;
    }, [settings, formData]);

    const handleInputChange = useCallback((section, field, value, subField = null) => {
        setFormData(prev => {
            const newData = { ...prev };
            const isRateField = section === "rate";
            const clamp = isRateField ? clampRate : clampValue;
            if (subField) {
                newData[section][field][subField] = clamp(value);
            } else if (field) {
                newData[section][field] = clamp(value);
            } else {
                newData[section] = clamp(value);
            }
            return newData;
        });
    }, []);

    const toggleEditMode = useCallback((section) => {
        setEditMode(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    }, []);

    const onSave = useCallback(async () => {
        if (!token) {
            toast.error("Session missing. Please login again.");
            return;
        }

        // Prepare payload with clamped values
        const payload = {
            trade_config: {
                win_rate: clampWinRate(formData.trade_config.win_rate)
            },
            commission: {
                staking: {
                    level_1: clampValue(formData.commission.staking.level_1),
                    level_2: clampValue(formData.commission.staking.level_2),
                    level_3: clampValue(formData.commission.staking.level_3),
                    level_4: clampValue(formData.commission.staking.level_4),
                    level_5: clampValue(formData.commission.staking.level_5),
                    level_6: clampValue(formData.commission.staking.level_6),
                    level_7: clampValue(formData.commission.staking.level_7),
                    level_8: clampValue(formData.commission.staking.level_8),
                    level_9: clampValue(formData.commission.staking.level_9),
                    level_10: clampValue(formData.commission.staking.level_10)
                },
                self_deposit: clampValue(formData.commission.self_deposit),
                refer: {
                    level_1: clampValue(formData.commission.refer.level_1),
                    level_2: clampValue(formData.commission.refer.level_2),
                    level_3: clampValue(formData.commission.refer.level_3),
                    level_4: clampValue(formData.commission.refer.level_4),
                    level_5: clampValue(formData.commission.refer.level_5),
                    level_6: clampValue(formData.commission.refer.level_6)
                },
                deposit: {
                    level_1: clampValue(formData.commission.deposit.level_1),
                    level_2: clampValue(formData.commission.deposit.level_2),
                    level_3: clampValue(formData.commission.deposit.level_3),
                    level_4: clampValue(formData.commission.deposit.level_4),
                    level_5: clampValue(formData.commission.deposit.level_5),
                    level_6: clampValue(formData.commission.deposit.level_6)
                }
            },
            rate: {
                coin: clampRate(formData.rate.coin),
                exchange: clampRate(formData.rate.exchange),
                exchange_sell: clampRate(formData.rate.exchange_sell),
            }
        };

        setSaving(true);
        try {
            const res = await axios({
                method: "post",
                url: `${BASE_ENDPOINT}/admin/update-settings`,
                headers: { "Content-Type": "application/json" },
                data: {
                    token,
                    data: payload
                },
                timeout: 15000,
            });

            const out = res?.data;

            if (out?.error) {
                if (out?.error === "session expired") {
                    toast.error("Session expired. Please login again.");
                } else {
                    toast.error(out?.msg || out?.error || "Failed to update settings");
                }
                return;
            }

            setSettings(out?.data || null);
            toast.success(out?.msg || "Settings updated successfully");

            // Reset all edit modes after successful save
            setEditMode({
                trade: false,
                selfDeposit: false,
                staking: false,
                refer: false,
                deposit: false,
                rate: false
            });

            await fetchSettings();
        } catch (err) {
            console.error(err);
            toast.error("Failed to update settings");
        } finally {
            setSaving(false);
        }
    }, [token, formData, fetchSettings]);

    const handleUpiFormChange = (field, value) => {
        setUpiForm((prev) => ({ ...prev, [field]: value }));
    };

    const onAddExchangeUpi = useCallback(async (e) => {
        e?.preventDefault?.();
        if (!token) {
            toast.error("Session missing. Please login again.");
            return;
        }

        const upi_id = String(upiForm.upi_id || "").trim();
        const provider = String(upiForm.provider || "").trim();
        const mid = String(upiForm.mid || "").trim();
        const min = Number(upiForm.min);
        const max = Number(upiForm.max);

        if (!upi_id || !provider || !mid) {
            toast.error("UPI ID, provider, and MID are required");
            return;
        }
        if (!Number.isFinite(min) || !Number.isFinite(max) || min <= 0 || max <= 0) {
            toast.error("Min and max must be greater than 0");
            return;
        }
        if (min > max) {
            toast.error("Min cannot be greater than max");
            return;
        }

        setAddingUpi(true);
        try {
            const res = await axios({
                method: "post",
                url: `${BASE_ENDPOINT}/admin/add-exchange-upi`,
                headers: { "Content-Type": "application/json" },
                data: { token, upi_id, min, max, provider, mid },
                timeout: 15000,
            });
            const out = res?.data;
            if (out?.error) {
                toast.error(
                    out?.error === "session expired"
                        ? "Session expired. Please login again."
                        : (typeof out?.error === "string" ? out.error : out?.msg || "Failed to add UPI")
                );
                return;
            }
            toast.success(out?.msg || "Exchange UPI added successfully");
            setUpiForm(EMPTY_UPI_FORM);
            await fetchSettings();
        } catch (err) {
            console.error(err);
            toast.error("Failed to add exchange UPI");
        } finally {
            setAddingUpi(false);
        }
    }, [token, upiForm, fetchSettings]);

    const onDeleteExchangeUpi = useCallback(async (id) => {
        if (!token) {
            toast.error("Session missing. Please login again.");
            return;
        }
        if (!id && id !== 0) {
            toast.error("Missing UPI id");
            return;
        }
        const ok = window.confirm("Delete this exchange UPI ID? This cannot be undone.");
        if (!ok) return;

        setDeletingUpiId(id);
        try {
            const res = await axios({
                method: "post",
                url: `${BASE_ENDPOINT}/admin/delete-exchange-upi`,
                headers: { "Content-Type": "application/json" },
                data: { token, id },
                timeout: 15000,
            });
            const out = res?.data;
            if (out?.error) {
                toast.error(
                    out?.error === "session expired"
                        ? "Session expired. Please login again."
                        : (typeof out?.error === "string" ? out.error : out?.msg || "Failed to delete UPI")
                );
                return;
            }
            toast.success(out?.msg || "Exchange UPI deleted successfully");
            await fetchSettings();
        } catch (err) {
            console.error(err);
            toast.error("Failed to delete exchange UPI");
        } finally {
            setDeletingUpiId(null);
        }
    }, [token, fetchSettings]);

    const renderLevelInputs = (title, levels, values, section, field, icon, editKey) => (
        <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white border-0 pt-3 pb-2">
                <div className="d-flex align-items-center justify-content-between">
                    <div className="fw-semibold d-flex align-items-center gap-2">
                        {icon}
                        {title}
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        {editMode[editKey] && (
                            <span className="badge bg-warning text-dark animate-pulse">
                                <small>Editing</small>
                            </span>
                        )}
                        <button
                            type="button"
                            className={`btn btn-sm ${editMode[editKey] ? 'btn-warning' : 'btn-outline-primary'}`}
                            onClick={() => toggleEditMode(editKey)}
                            disabled={loading || saving || refreshing}
                            style={{ fontSize: '0.75rem' }}
                        >
                            {editMode[editKey] ? (
                                <>
                                    <FaLockOpen size={12} className="me-1" />
                                    Editing
                                </>
                            ) : (
                                <>
                                    <FaEdit size={12} className="me-1" />
                                    Edit
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
            <div className="card-body">
                <div className="row row-cols-2 row-cols-md-3 row-cols-lg-5 g-3">
                    {levels.map((level, index) => (
                        <div className="col" key={level}>
                            <div className="card h-100 border-light">
                                <div className="card-body text-center p-3">
                                    <div className="text-muted small mb-1">
                                        Level {index + 1}
                                    </div>
                                    <div className="input-group input-group-sm">
                                        <input
                                            type="number"
                                            className={`form-control text-center ${editMode[editKey] ? 'border-warning' : 'border-light'}`}
                                            value={values[level] || 0}
                                            onChange={(e) => handleInputChange(section, field, e.target.value, level)}
                                            onWheel={(e) => e.preventDefault()}
                                            disabled={loading || saving || refreshing || !editMode[editKey]}
                                            min="0"
                                            max="100"
                                            step="0.1"
                                            style={{
                                                fontWeight: "500",
                                                backgroundColor: editMode[editKey] ? '#fffaf0' : '#f8f9fa'
                                            }}
                                        />
                                        <span className={`input-group-text ${editMode[editKey] ? 'bg-warning text-dark' : 'bg-light'}`}>%</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    return (
        <PageTransition>
            <div className="row g-4">
                <div className="col-12">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                        <div>
                            <h2 className="tb-page-title mb-0 d-flex align-items-center gap-2">
                                <FaCog className="text-primary" /> System Settings
                            </h2>
                            <p className="tb-page-subtitle mb-0">Configure trade, rates, and commission settings</p>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <button
                                className="btn btn-outline-secondary d-inline-flex align-items-center gap-2"
                                type="button"
                                onClick={onRefresh}
                                disabled={loading || refreshing || saving}
                                title="Refresh"
                            >
                                <FaSyncAlt className={refreshing ? "tb-spin" : ""} />
                                Refresh
                            </button>

                            <button
                                className="btn btn-primary d-inline-flex align-items-center gap-2"
                                type="button"
                                onClick={onSave}
                                disabled={loading || refreshing || saving || !settings || !isDirty}
                                title="Save"
                            >
                                {saving ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <FaSave />
                                        Save Changes
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Trade Configuration */}
                <div className="col-12">
                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white border-0 pt-3 pb-2">
                            <div className="d-flex align-items-center justify-content-between gap-2">
                                <div className="fw-semibold d-flex align-items-center gap-2">
                                    <FaChartLine className="text-primary" />
                                    Trade Configuration
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    {editMode.trade && (
                                        <span className="badge bg-warning text-dark animate-pulse">
                                            <small>Editing</small>
                                        </span>
                                    )}
                                    <button
                                        type="button"
                                        className={`btn btn-sm ${editMode.trade ? 'btn-warning' : 'btn-outline-primary'}`}
                                        onClick={() => toggleEditMode('trade')}
                                        disabled={loading || saving || refreshing}
                                        style={{ fontSize: '0.75rem' }}
                                    >
                                        {editMode.trade ? (
                                            <>
                                                <FaLockOpen size={12} className="me-1" />
                                                Editing
                                            </>
                                        ) : (
                                            <>
                                                <FaEdit size={12} className="me-1" />
                                                Edit
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="card-body">
                            {loading ? (
                                <div className="placeholder-glow">
                                    <div className="mb-3">
                                        <span className="placeholder col-4"></span>
                                    </div>
                                    <div className="mb-2">
                                        <span className="placeholder col-12"></span>
                                    </div>
                                </div>
                            ) : !settings ? (
                                <div className="text-center text-muted py-4">
                                    <FaCog className="mb-3" size={48} />
                                    <p>Failed to load settings</p>
                                </div>
                            ) : (
                                <div className="row align-items-center">
                                    <div className="col-md-8">
                                        <label className="form-label fw-semibold d-flex align-items-center gap-2">
                                            <FaPercentage /> Trade Win Rate
                                        </label>
                                        <p className="text-muted small mb-3">
                                            Adjust the win probability percentage for trading operations
                                        </p>
                                        <input
                                            type="range"
                                            className="form-range"
                                            min={1}
                                            max={99}
                                            value={formData.trade_config.win_rate}
                                            onChange={(e) => handleInputChange('trade_config', 'win_rate', e.target.value)}
                                            disabled={saving || refreshing || !editMode.trade}
                                            style={{
                                                opacity: editMode.trade ? 1 : 0.6,
                                                backgroundColor: editMode.trade ? '#fffaf0' : '#f8f9fa'
                                            }}
                                        />
                                    </div>
                                    <div className="col-md-4 text-center">
                                        <div className="display-4 fw-bold text-primary">
                                            {clampWinRate(formData.trade_config.win_rate)}%
                                        </div>
                                        <small className="text-muted">Current Value</small>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Rate Settings */}
                <div className="col-12">
                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white border-0 pt-3 pb-2">
                            <div className="d-flex align-items-center justify-content-between gap-2">
                                <div className="fw-semibold d-flex align-items-center gap-2">
                                    <FaExchangeAlt className="text-primary" />
                                    Rate Settings
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    {editMode.rate && (
                                        <span className="badge bg-warning text-dark animate-pulse">
                                            <small>Editing</small>
                                        </span>
                                    )}
                                    <button
                                        type="button"
                                        className={`btn btn-sm ${editMode.rate ? "btn-warning" : "btn-outline-primary"}`}
                                        onClick={() => toggleEditMode("rate")}
                                        disabled={loading || saving || refreshing}
                                        style={{ fontSize: "0.75rem" }}
                                    >
                                        {editMode.rate ? (
                                            <>
                                                <FaLockOpen size={12} className="me-1" />
                                                Editing
                                            </>
                                        ) : (
                                            <>
                                                <FaEdit size={12} className="me-1" />
                                                Edit
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                            <small className="text-muted">Coin and exchange buy/sell conversion rates</small>
                        </div>
                        <div className="card-body">
                            {loading ? (
                                <div className="placeholder-glow">
                                    <div className="row g-3">
                                        <div className="col-md-4">
                                            <span className="placeholder col-12" style={{ height: 48 }} />
                                        </div>
                                        <div className="col-md-4">
                                            <span className="placeholder col-12" style={{ height: 48 }} />
                                        </div>
                                        <div className="col-md-4">
                                            <span className="placeholder col-12" style={{ height: 48 }} />
                                        </div>
                                    </div>
                                </div>
                            ) : !settings ? (
                                <div className="text-center text-muted py-3">Failed to load rate settings</div>
                            ) : (
                                <div className="row g-4">
                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold d-flex align-items-center gap-2">
                                            <FaCoins className="text-primary" />
                                            Coin Rate
                                        </label>
                                        <p className="text-muted small mb-2">Platform coin rate value</p>
                                        <input
                                            type="number"
                                            className={`form-control form-control-lg ${editMode.rate ? "border-warning" : "border-light"}`}
                                            value={formData.rate.coin}
                                            onChange={(e) => handleInputChange("rate", "coin", e.target.value)}
                                            onWheel={(e) => e.preventDefault()}
                                            disabled={loading || saving || refreshing || !editMode.rate}
                                            min="0"
                                            step="0.01"
                                            style={{
                                                backgroundColor: editMode.rate ? "#fffaf0" : "#f8f9fa",
                                            }}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold d-flex align-items-center gap-2">
                                            <FaExchangeAlt className="text-primary" />
                                            Exchange Buy Rate
                                        </label>
                                        <p className="text-muted small mb-2">INR → USDT (buy / deposit)</p>
                                        <input
                                            type="number"
                                            className={`form-control form-control-lg ${editMode.rate ? "border-warning" : "border-light"}`}
                                            value={formData.rate.exchange}
                                            onChange={(e) => handleInputChange("rate", "exchange", e.target.value)}
                                            onWheel={(e) => e.preventDefault()}
                                            disabled={loading || saving || refreshing || !editMode.rate}
                                            min="0"
                                            step="0.01"
                                            style={{
                                                backgroundColor: editMode.rate ? "#fffaf0" : "#f8f9fa",
                                            }}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold d-flex align-items-center gap-2">
                                            <FaExchangeAlt className="text-success" />
                                            Exchange Sell / Withdrawal Rate
                                        </label>
                                        <p className="text-muted small mb-2">USDT → INR (sell / withdrawal)</p>
                                        <input
                                            type="number"
                                            className={`form-control form-control-lg ${editMode.rate ? "border-warning" : "border-light"}`}
                                            value={formData.rate.exchange_sell}
                                            onChange={(e) => handleInputChange("rate", "exchange_sell", e.target.value)}
                                            onWheel={(e) => e.preventDefault()}
                                            disabled={loading || saving || refreshing || !editMode.rate}
                                            min="0"
                                            step="0.01"
                                            style={{
                                                backgroundColor: editMode.rate ? "#fffaf0" : "#f8f9fa",
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Exchange UPI IDs */}
                <div className="col-12">
                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white border-0 pt-3 pb-2">
                            <div className="fw-semibold d-flex align-items-center gap-2">
                                <FaUniversity className="text-primary" />
                                Exchange UPI IDs
                            </div>
                            <small className="text-muted">
                                Manage buy-side exchange UPI accounts (add/delete via separate APIs)
                            </small>
                        </div>
                        <div className="card-body">
                            {loading ? (
                                <div className="placeholder-glow">
                                    <span className="placeholder col-12" style={{ height: 80 }} />
                                </div>
                            ) : (
                                <>
                                    <form onSubmit={onAddExchangeUpi} className="border rounded-3 p-3 mb-4 bg-light">
                                        <div className="fw-semibold mb-3 d-flex align-items-center gap-2">
                                            <FaPlus className="text-primary" />
                                            Add Exchange UPI
                                        </div>
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <label className="form-label small text-muted mb-1">UPI ID</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={upiForm.upi_id}
                                                    onChange={(e) => handleUpiFormChange("upi_id", e.target.value)}
                                                    placeholder="example@upi"
                                                    disabled={addingUpi || refreshing}
                                                    required
                                                />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label small text-muted mb-1">Provider</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={upiForm.provider}
                                                    onChange={(e) => handleUpiFormChange("provider", e.target.value)}
                                                    placeholder="Provider Name"
                                                    disabled={addingUpi || refreshing}
                                                    required
                                                />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label small text-muted mb-1">MID</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={upiForm.mid}
                                                    onChange={(e) => handleUpiFormChange("mid", e.target.value)}
                                                    placeholder="MID123456"
                                                    disabled={addingUpi || refreshing}
                                                    required
                                                />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label small text-muted mb-1">Min</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={upiForm.min}
                                                    onChange={(e) => handleUpiFormChange("min", e.target.value)}
                                                    min="1"
                                                    step="1"
                                                    disabled={addingUpi || refreshing}
                                                    required
                                                />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label small text-muted mb-1">Max</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={upiForm.max}
                                                    onChange={(e) => handleUpiFormChange("max", e.target.value)}
                                                    min="1"
                                                    step="1"
                                                    disabled={addingUpi || refreshing}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="d-flex justify-content-end mt-3">
                                            <button
                                                type="submit"
                                                className="btn btn-primary d-inline-flex align-items-center gap-2"
                                                disabled={addingUpi || refreshing}
                                            >
                                                {addingUpi ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm" />
                                                        Adding...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FaPlus />
                                                        Add UPI
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </form>

                                    {exchangeUpiIds.length === 0 ? (
                                        <div className="text-center text-muted py-4">
                                            No exchange UPI IDs configured
                                        </div>
                                    ) : (
                                        <div className="row g-3">
                                            {exchangeUpiIds.map((item) => (
                                                <div className="col-12 col-lg-6" key={item.id ?? item.upi_id}>
                                                    <div className="border rounded-3 p-3 h-100">
                                                        <div className="d-flex justify-content-between align-items-start gap-2">
                                                            <div>
                                                                <div className="fw-semibold text-break">{item.upi_id || "—"}</div>
                                                                <div className="small text-muted">
                                                                    {item.provider || "—"} · MID: {item.mid || "—"}
                                                                </div>
                                                                <div className="small mt-1">
                                                                    Min ₹{Number(item.min || 0).toLocaleString("en-IN")} · Max ₹
                                                                    {Number(item.max || 0).toLocaleString("en-IN")}
                                                                </div>
                                                                <div className="small text-muted mt-1">ID: {item.id}</div>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => onDeleteExchangeUpi(item.id)}
                                                                disabled={deletingUpiId === item.id || refreshing}
                                                                title="Delete UPI"
                                                            >
                                                                {deletingUpiId === item.id ? (
                                                                    <span className="spinner-border spinner-border-sm" />
                                                                ) : (
                                                                    <FaTrash />
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Commission Settings */}
                <div className="col-12">
                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white border-0 pt-3 pb-2">
                            <div className="d-flex align-items-center justify-content-between">
                                <div className="fw-semibold d-flex align-items-center gap-2">
                                    <FaMoneyBillWave className="text-primary" />
                                    Commission Settings
                                </div>
                                {!loading && settings && (
                                    <div>
                                        {isDirty ? (
                                            <span className="badge bg-warning text-dark animate-pulse">
                                                <small>Unsaved Changes</small>
                                            </span>
                                        ) : (
                                            <span className="badge bg-success">
                                                <small>All Saved</small>
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                            <small className="text-muted">Configure commission percentages for different activities</small>
                        </div>

                        <div className="card-body">
                            {loading ? (
                                <div className="placeholder-glow">
                                    {[1, 2, 3, 4].map(i => (
                                        <div key={i} className="mb-4">
                                            <span className="placeholder col-3 mb-2"></span>
                                            <div className="row">
                                                {[1, 2, 3, 4, 5].map(j => (
                                                    <div key={j} className="col mb-2">
                                                        <span className="placeholder col-12" style={{ height: '60px' }}></span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : !settings ? (
                                <div className="text-center text-muted py-4">
                                    Failed to load commission settings
                                </div>
                            ) : (
                                <>
                                    {/* Self Deposit Commission */}
                                    <div className="card border-0 shadow-sm mb-4 bg-light">
                                        <div className="card-body">
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                <div>
                                                    <label className="form-label fw-semibold d-flex align-items-center gap-2">
                                                        <FaMoneyBillWave /> Self Deposit Commission
                                                    </label>
                                                    <p className="text-muted small mb-0">
                                                        Commission percentage for self deposits
                                                    </p>
                                                </div>
                                                <div className="d-flex align-items-center gap-2">
                                                    {editMode.selfDeposit && (
                                                        <span className="badge bg-warning text-dark animate-pulse">
                                                            <small>Editing</small>
                                                        </span>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className={`btn btn-sm ${editMode.selfDeposit ? 'btn-warning' : 'btn-outline-primary'}`}
                                                        onClick={() => toggleEditMode('selfDeposit')}
                                                        disabled={loading || saving || refreshing}
                                                        style={{ fontSize: '0.75rem' }}
                                                    >
                                                        {editMode.selfDeposit ? (
                                                            <>
                                                                <FaLockOpen size={12} className="me-1" />
                                                                Editing
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FaEdit size={12} className="me-1" />
                                                                Edit
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="row align-items-center">
                                                <div className="col-md-8">
                                                    <input
                                                        type="number"
                                                        className={`form-control form-control-lg ${editMode.selfDeposit ? 'border-warning' : 'border-light'}`}
                                                        value={formData.commission.self_deposit}
                                                        onChange={(e) => handleInputChange('commission', 'self_deposit', e.target.value)}
                                                        onWheel={(e) => e.preventDefault()}
                                                        disabled={loading || saving || refreshing || !editMode.selfDeposit}
                                                        min="0"
                                                        max="100"
                                                        step="0.1"
                                                        style={{
                                                            backgroundColor: editMode.selfDeposit ? '#fffaf0' : '#f8f9fa'
                                                        }}
                                                    />
                                                </div>
                                                <div className="col-md-4">
                                                    <div className="input-group">
                                                        <span className="input-group-text bg-transparent border-0 fw-semibold">
                                                            Current: {formData.commission.self_deposit}%
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Staking Commission Levels */}
                                    {renderLevelInputs(
                                        "Staking Commission Levels",
                                        stakingLevels,
                                        formData.commission.staking,
                                        'commission',
                                        'staking',
                                        <FaLayerGroup className="text-primary" />,
                                        'staking'
                                    )}

                                    {/* Referral Commission Levels */}
                                    {renderLevelInputs(
                                        "Referral Commission Levels",
                                        referDepositLevels,
                                        formData.commission.refer,
                                        'commission',
                                        'refer',
                                        <FaUsers className="text-primary" />,
                                        'refer'
                                    )}

                                    {/* Deposit Commission Levels */}
                                    {renderLevelInputs(
                                        "Deposit Commission Levels",
                                        referDepositLevels,
                                        formData.commission.deposit,
                                        'commission',
                                        'deposit',
                                        <FaMoneyBillWave className="text-primary" />,
                                        'deposit'
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Summary Card */}
                {!loading && settings && (
                    <div className="col-12">
                        <div className="card border-0 shadow-sm bg-gradient-primary text-white">
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-6 col-md text-center mb-3 mb-md-0">
                                        <div className="display-6 fw-bold">{clampWinRate(formData.trade_config.win_rate)}%</div>
                                        <small className="opacity-75">Win Rate</small>
                                    </div>
                                    <div className="col-6 col-md text-center mb-3 mb-md-0">
                                        <div className="display-6 fw-bold">{clampRate(formData.rate.coin)}</div>
                                        <small className="opacity-75">Coin Rate</small>
                                    </div>
                                    <div className="col-6 col-md text-center mb-3 mb-md-0">
                                        <div className="display-6 fw-bold">{clampRate(formData.rate.exchange)}</div>
                                        <small className="opacity-75">Buy Rate</small>
                                    </div>
                                    <div className="col-6 col-md text-center mb-3 mb-md-0">
                                        <div className="display-6 fw-bold">{clampRate(formData.rate.exchange_sell)}</div>
                                        <small className="opacity-75">Sell Rate</small>
                                    </div>
                                    <div className="col-6 col-md text-center mb-3 mb-md-0">
                                        <div className="display-6 fw-bold">{formData.commission.self_deposit}%</div>
                                        <small className="opacity-75">Self Deposit</small>
                                    </div>
                                    <div className="col-6 col-md text-center">
                                        <div className="display-6 fw-bold">{formData.commission.refer.level_1}%</div>
                                        <small className="opacity-75">Direct Referral</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </PageTransition>
    );
}